import type { OrderDetailsUI } from "@/domain/order/order-details.model"
import { resolvePaymentPresentationFromOrder } from "@/domain/order/order-details-payment-presentation"

/** Collapsed secondary line: `{Section label} • {summary}`. */
export function disclosureCollapsedLine(sectionLabel: string, summary: string): string {
  const L = sectionLabel.trim()
  const S = summary.trim()
  if (!S) return L
  return `${L} • ${S}`
}

/**
 * Returns last four digits only when the value has at least four digit characters;
 * otherwise null (avoids leaking short or ambiguous identifiers).
 */
export function getPaymentLastFourDigitsSafe(raw: string | null | undefined): string | null {
  if (raw == null || raw === "") return null
  const digits = raw.replace(/\D/g, "")
  if (digits.length < 4) return null
  return digits.slice(-4)
}

/**
 * One-line summary fragment for payment (method + optional last 4). No full IDs.
 */
export function getPaymentCollapsedSummary(order: OrderDetailsUI): string {
  const presentation = resolvePaymentPresentationFromOrder(order)

  if (!presentation.showTransactionDetails) {
    if (presentation.paymentMethodLabel) {
      return `${presentation.statusLabel} · ${presentation.paymentMethodLabel}`
    }
    return presentation.statusLabel
  }

  const via = (presentation.paymentMethodLabel || "Payment").trim() || "Payment"
  const last4 = getPaymentLastFourDigitsSafe(order.paymentId ?? "")
  if (last4) return `Paid via ${via} · •••• ${last4}`
  return `Paid via ${via}`
}

/** Timeline summary fragment (pair with `disclosureCollapsedLine("Timeline", …)`). */
export function getTimelineCollapsedSummary(statusLabel: string, eventCount: number): string {
  const label = statusLabel.trim() || "Order"
  return `${eventCount} event${eventCount === 1 ? "" : "s"} · ${label}`
}
